// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { doScroll } from "@pace/XDL/Utilities/scroll";
import xDefaultRules from "@pace/XDL/Utilities/xValidate/xDefaultRules";
import validate from "@pace/XDL/Utilities/xValidate/xValidate";
import debounce from "lodash/debounce";

export interface xValidateFieldParams {
    label: string;
    onError?: React.Dispatch<React.SetStateAction<string>> | ((error: string) => void);
    validationRules?: string;
    type?: keyof typeof xDefaultRules;
    onBlur?: (event: any) => void;
    value: any;
    required?: boolean;
    touched?: boolean;
}

export interface xValidationProps extends Pick<xValidateFieldParams, "validationRules"> {
    formId?: string;
    disableMask?: boolean;
    messageLabel?: string;
}

const validatorStore: {
    [key: string]: {
        fields: {
            [key: string]: xValidateFieldParams;
        };
        locked: boolean;
        submittedBefore: boolean;
    };
} = {};

const validateField = (
    key: string,
    value: any,
    validationRules?: string,
    type: keyof typeof xDefaultRules = "text",
    required = false,
) => {
    const schema = validationRules || xDefaultRules[type];
    const error = validate(value, schema, key, required);
    return error;
};

const lockForm = debounce(id => {
    if (!validatorStore[id]) {
        validatorStore[id] = { locked: false, fields: {}, submittedBefore: false };
    }
    validatorStore[id].locked = true;
    // we need purge to remove all the locked forms other that current, from old pages
}, 1000);

const xFieldValidator = (params: xValidateFieldParams, formId = "default") => {
    const { label, onError, validationRules, type = "text", onBlur, required, touched } = params;

    const otherProps = {
        label,
        type,
    };
    validatorStore[formId] = validatorStore[formId] || { fields: {}, locked: false, submittedBefore: false };
    // if (validatorStore[formId].locked) {
    //     validatorStore[formId] = { fields: {}, locked: false, submittedBefore: validatorStore[formId].submittedBefore };
    // }
    validatorStore[formId].fields[params.label] = params;

    lockForm(formId);

    return {
        onBlur: (event: any) => {
            const value = event.target.value;
            const error = validateField(label, value, validationRules, type, required);
            onError?.(error);
            onBlur?.(event);
        },
        onKeyUp: (event: any) => {
            if (touched || validatorStore[formId].submittedBefore) {
                const value = event.target.value;
                const error = validateField(label, value, validationRules, type, required);
                onError?.(error);
            }
        },
        ...otherProps,
    };
};

const xValidateOnSubmit = (formId = "default") => {
    const { fields } = validatorStore[formId];
    validatorStore[formId].submittedBefore = true;

    const errors: string[] = [];
    Object.keys(fields).forEach(key => {
        const { value, type = "text", validationRules, onError, required } = fields[key];
        const error = validateField(key, value, validationRules, type, required);
        onError?.(error);
        if (error) {
            errors.push(error);
        }
    });
    if (errors.length > 0) {
        doScroll();
    }
    return errors;
};

export default xFieldValidator;
export { xFieldValidator, xValidateOnSubmit };

